import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { GridApi, ModelUpdatedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { ToggleButton } from 'primereact/togglebutton';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useLazySearchSiresQuery } from '@/app/services/lookups';
import { addToast } from '@/app/slices/toast';
import { columnDefs } from '@/components/common/advanced-sire-lookup/column-defs';
import { EditSireDialog } from '@/components/common/edit-sire-dialog';
import { NewSireDialog } from '@/components/common/new-sire-dialog/NewSireDialog';
import { WithLocalStorage } from '@/components/hoc';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button as UiButton } from '@/components/ui/button';
import {
  Card as UiCard,
  CardContent as UiCardContent,
} from '@/components/ui/card';
import { Checkbox as UiCheckbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef, lookupSideBar, sideBar } from '@/grid/defaults';
import { getRowId } from '@/grid/types';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { Sire } from '@/rtk-query.gql';
import { appendRevisionComment } from '@/utils/formatting';
import { getLocalStorage } from '@/utils/storage';

import { useAdvancedSireDataSource } from './useAdvancedSireDataSource';

type IAdvancedSireLookupProps = {
  className?: string;
  label?: string;
  value?: Sire;
  ownerId?: string;
  onChange?: (payload: Sire) => void;
  onClear?: () => void;
  revisionComment?: string;
  setRevisionComment?: (revisionComment: string) => void;
  revisionNumber?: {
    previousRevision: string;
    currentRevision: string;
  };
  setRevisionNumber?: (payload: {
    previousRevision: string;
    currentRevision: string;
  }) => void;
  tooltip?: string;
  tabIndex?: number;
  editable?: boolean;
  disabled?: boolean;
  showAddButton?: boolean;
  showEditButton?: boolean;
};

const DATA_TABLE_ROWS = 50;
const CACHE_BLOCK_SIZE = 500;
const LOAD_DEBOUNCE_MS = 500;

export const AdvancedSireLookup = ({
  className,
  value,
  ownerId,
  label,
  onChange,
  onClear,
  revisionComment,
  setRevisionComment,
  revisionNumber,
  setRevisionNumber,
  tooltip,
  tabIndex,
  editable = false,
  disabled = false,
  showAddButton = true,
  showEditButton = true,
}: IAdvancedSireLookupProps) => {
  const storageKey = 'sire-lookup';
  const { t } = useTranslation();
  const defaultGridOptions = useDefaultGridOptions();
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const sireInfiniteScroll = useFeatureFlag('Sire_Infinite_Scroll');
  const isSearchResetEnabled = useFeatureFlag('DS_SemenSireDonor_Search_Reset');
  const isSpecificViewContextEnabled = useFeatureFlag('Specific_View_Context');
  const isSireEditRefreshEnabled = useFeatureFlag('Sire_Edit_Refresh');
  const isBaseUiSearchEnabled = useFeatureFlag('Account_Combobox_not_Dropdown');
  const ALL_OPTION = { label: t('common.all'), value: '' };
  const { workingDonorSchedule } = useAppSelector(
    (state) => state.donorScheduling,
  );
  const mode = useAppSelector((state) => state.preferences.mode);

  const SEARCH_BY_OPTIONS = columnDefs.map(({ headerName, field }) => ({
    label: headerName,
    value: field,
  }));

  const SEARCH_BY_OPTIONS_WITH_ALL = [ALL_OPTION, ...SEARCH_BY_OPTIONS];

  const gridRef = useRef<AgGridReact>(null);
  // Id of the sire just saved, waiting for the grid refresh to return its row.
  const pendingSavedSireIdRef = useRef<string | undefined>(undefined);

  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [dialogContainer, setDialogContainer] = useState<HTMLElement | null>(
    null,
  );
  const [searchTermTemp, setSearchTermTemp] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedSearchBy, setSelectedSearchBy] = useState(ALL_OPTION.value);
  const [selectedSire, setSelectedSire] = useState(value);
  const [searchAllSires, setSearchAllSires] = useState(false);
  const [inputValue, setInputValue] = useState<string>(value?.name || '');
  const [newSireDialogVisible, setNewSireDialogVisible] = useState(false);
  const [editSireDialogVisible, setEditSireDialogVisible] = useState(false);
  const [selectedSireForEdit, setSelectedSireForEdit] = useState<
    Sire | undefined
  >();
  const [filterByRanch, setFilterByRanch] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(false);

  const isDonorSchedulingApp = pathname.startsWith('/donor-scheduling');

  useEffect(() => {
    setInputValue(value?.name || '');
    setSelectedSire(value);
  }, [value]);

  useEffect(() => {
    setSelectedSireForEdit(selectedSire);
  }, [selectedSire?.id]);

  const [searchSiresQuery] = useLazySearchSiresQuery();
  const { dataSource, refetch } = useAdvancedSireDataSource({
    query: searchSiresQuery,
    searchBy: selectedSearchBy,
    searchTerm,
    ownerId: filterByRanch ? ownerId : undefined,
    gridRef,
    refetchTrigger,
  });

  const noRowsOverlayMessage = t('sire.noMatchingSireFound');

  const memoizedDefaultColDef = useMemo(
    () => ({
      ...defaultColDef,
      filter: true,
      filterParams: {
        buttons: ['apply', 'clear'],
        closeOnApply: true,
      },
    }),
    [],
  );

  const showNewlyAddedColumns = useCallback((api: GridApi) => {
    const gridState = getLocalStorage(storageKey);
    if (!Array.isArray(gridState?.columnState)) {
      return;
    }

    const savedColumnIds = new Set(
      gridState.columnState
        .map((column: { colId?: string }) => column.colId)
        .filter((colId: string | undefined): colId is string => !!colId),
    );
    const newlyAddedColumnIds = api
      .getColumnState()
      .map((column) => column.colId)
      .filter(
        (colId): colId is string => !!colId && !savedColumnIds.has(colId),
      );

    if (newlyAddedColumnIds.length > 0) {
      api.setColumnsVisible(newlyAddedColumnIds, true);
    }
  }, []);

  const closeDialog = useCallback(() => {
    if (isSearchResetEnabled) {
      setSelectedSearchBy(ALL_OPTION.value);
      setSearchTermTemp('');
      setSearchTerm('');
      setFilterByRanch(false);
      gridRef.current?.api?.setFilterModel(null);
    }
    setDialogVisible(false);
  }, [ALL_OPTION.value, isSearchResetEnabled]);

  const dialogFooterTemplate = (disabled: boolean) => {
    return (
      <div className="flex w-full justify-between">
        <div className="flex gap-2">
          <UiButton
            variant="outline"
            onClick={() => setNewSireDialogVisible(true)}
            type="button"
          >
            {t('common.new')}
          </UiButton>
          <UiButton
            variant="ghost"
            disabled={selectedSire === undefined}
            onClick={() => {
              const sireToEdit = isSireEditRefreshEnabled
                ? (gridRef.current?.api?.getSelectedNodes()[0]?.data ??
                  selectedSire)
                : selectedSire;
              if (sireToEdit) {
                setSelectedSire(sireToEdit);
                setSelectedSireForEdit(sireToEdit);
              }
              setEditSireDialogVisible(true);
            }}
            type="button"
          >
            {t('common.edit')}
          </UiButton>
        </div>
        <div className="flex gap-2">
          <UiButton variant="outline" onClick={closeDialog} type="button">
            {t('common.cancel')}
          </UiButton>
          <UiButton
            type="button"
            onClick={() => {
              closeDialog();
              if (selectedSire) {
                if (params.get('revision') === 'true') {
                  const newNoteText = `Change from Sire, ${workingDonorSchedule?.sire?.name} to Sire, ${selectedSire?.name}`;
                  setRevisionComment(
                    appendRevisionComment(revisionComment, newNoteText),
                  );
                  dispatch(
                    addToast({
                      severity: 'info',
                      summary: t('revision.revisionFieldChanged'),
                      detail: t('revision.pleaseUpdateRevisionNumber'),
                      life: ToastLife.MEDIUM,
                    }),
                  );
                }
                setInputValue(selectedSire?.name || '');
                onChange?.(selectedSire);
              }
            }}
            disabled={disabled}
          >
            {t('common.select')}
          </UiButton>
        </div>
      </div>
    );
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement> | undefined) => {
    if (e) {
      e.preventDefault();
    }

    setSearchTerm(searchTermTemp);
  };

  const handleSelectSire = () => {
    if (selectedSire) {
      onChange?.(selectedSire);
      closeDialog();
    }
  };

  const handleSelectionChanged = (e) => {
    const selectedNode = e.api.getSelectedNodes()[0];

    if (selectedNode && selectedNode.data) {
      setSelectedSire(selectedNode.data);
    } else {
      setSelectedSire(undefined);
    }
  };

  const handleOpenDialog = () => {
    setSearchTermTemp(inputValue);
    setSearchTerm(inputValue);
    setDialogVisible(true);
  };

  const handleEditSire = (event: { node: { data: Sire } }) => {
    const selectedNode = event.node;
    if (selectedNode && selectedNode.data) {
      setSelectedSire(selectedNode.data);
      setSelectedSireForEdit(selectedNode.data);
      if (refetchTrigger) {
        setRefetchTrigger(false);
      }
      setEditSireDialogVisible(true);
    }
  };

  const handleSaveSire = () => {
    setEditSireDialogVisible(false);
    if (isSireEditRefreshEnabled) {
      pendingSavedSireIdRef.current = selectedSireForEdit?.id ?? undefined;
      refetch();
      return;
    }
    setRefetchTrigger(true);
  };

  const handleModelUpdated = (event: ModelUpdatedEvent) => {
    const savedSireId = pendingSavedSireIdRef.current;
    if (!savedSireId) return;

    const savedSire = event.api.getRowNode(savedSireId)?.data as
      | Sire
      | undefined;
    if (!savedSire) return;

    pendingSavedSireIdRef.current = undefined;
    setSelectedSire((current) =>
      current?.id === savedSireId ? savedSire : current,
    );
    setSelectedSireForEdit((current) =>
      current?.id === savedSireId ? savedSire : current,
    );
  };

  const persistedGrid = (
    <AgGridReact
      gridOptions={defaultGridOptions}
      className={`ag-theme-transova${mode === 'dark' ? '-dark' : ''} h-full`}
      ref={gridRef}
      defaultColDef={memoizedDefaultColDef}
      columnDefs={columnDefs}
      getRowId={getRowId<Sire>((params) => String(params.data.id))}
      rowModelType="serverSide"
      rowSelection="single"
      cacheBlockSize={CACHE_BLOCK_SIZE}
      blockLoadDebounceMillis={LOAD_DEBOUNCE_MS}
      maintainColumnOrder={true}
      pagination={!sireInfiniteScroll}
      paginationAutoPageSize={!sireInfiniteScroll}
      serverSideDatasource={dataSource ?? undefined}
      paginationPageSize={DATA_TABLE_ROWS}
      onSelectionChanged={handleSelectionChanged}
      onModelUpdated={isSireEditRefreshEnabled ? handleModelUpdated : undefined}
      onCellDoubleClicked={editable ? handleEditSire : handleSelectSire}
      sideBar={
        isSpecificViewContextEnabled
          ? lookupSideBar('advanced-sire-lookup')
          : sideBar
      }
      overlayNoRowsTemplate={noRowsOverlayMessage}
      overlayLoadingTemplate={t('common.loading')}
    />
  );

  return (
    <div className={`label-input-group col-span-2`}>
      <div className="grid grid-cols-[auto_min-content_min-content] items-end gap-2">
        <div className="label-input-group">
          <label htmlFor="sire-input">{label}</label>
          <InputText
            className={className}
            id="sire-input"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleOpenDialog();
              }
            }}
            placeholder={t('sire.searchSire')}
            tabIndex={tabIndex}
            tooltip={tooltip}
            data-skip-enter-advance
            disabled={disabled}
          />
        </div>
        <div className="flex items-end gap-2">
          <Button
            icon="pi pi-search"
            onClick={handleOpenDialog}
            type="button"
            disabled={disabled}
            aria-label={t('sire.searchSire')}
          />
          {showEditButton && (
            <Button
              icon="pi pi-pencil"
              disabled={selectedSire === undefined}
              onClick={() => setEditSireDialogVisible(true)}
              type="button"
              aria-label={t('common.edit')}
            />
          )}
          {showAddButton && (
            <Button
              icon="pi pi-plus"
              onClick={() => setNewSireDialogVisible(true)}
              type="button"
              aria-label={t('common.new')}
            />
          )}
          {ownerId ? (
            <ToggleButton
              offLabel={t('common.all')}
              onLabel={t('common.all')}
              onIcon="pi pi-check"
              offIcon="pi pi-times"
              checked={searchAllSires}
              onChange={(e) => setSearchAllSires(e.value)}
            />
          ) : null}
          {onClear && (
            <Button
              type="button"
              icon="pi pi-times"
              severity="secondary"
              text
              disabled={disabled || !selectedSire}
              tabIndex={-1}
              onClick={onClear}
              aria-label="Clear sire"
            />
          )}
        </div>
      </div>

      <NewSireDialog
        visible={newSireDialogVisible}
        onHide={() => setNewSireDialogVisible(false)}
        onSave={handleSaveSire}
        gridRef={gridRef}
        revisionComment={revisionComment}
        setRevisionComment={setRevisionComment}
        revisionNumber={revisionNumber}
        setRevisionNumber={setRevisionNumber}
      />
      <EditSireDialog
        visible={editSireDialogVisible}
        onHide={() => setEditSireDialogVisible(false)}
        onSave={handleSaveSire}
        sire={selectedSireForEdit}
      />
      <Dialog
        open={dialogVisible}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent
          ref={setDialogContainer}
          className="z-[1500] h-[95vh] md:h-[95vh] md:max-w-[75vw]"
          overlayClassName="z-[1500]"
        >
          <DialogHeader>
            <DialogTitle>{t('sire.sireLookup')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="min-h-0">
            <div className="flex h-full flex-col">
              {isBaseUiSearchEnabled ? (
                <UiCard className="m-2 w-80">
                  <UiCardContent className="px-4 pt-4 pb-2">
                    <form
                      className="flex flex-col gap-3"
                      onSubmit={handleSearch}
                    >
                      <div className="label-input-group">
                        <label>{t('common.searchBy')}</label>
                        <Select
                          value={selectedSearchBy}
                          onChange={(v) => setSelectedSearchBy(v as string)}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue>
                              {SEARCH_BY_OPTIONS_WITH_ALL.find(
                                (o) => o.value === selectedSearchBy,
                              )?.label ?? ALL_OPTION.label}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent
                            container={dialogContainer ?? undefined}
                          >
                            {SEARCH_BY_OPTIONS_WITH_ALL.map((o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="label-input-group">
                        <label>{t('common.search')}</label>
                        <div className="grid grid-cols-[auto_min-content] items-center gap-x-2">
                          <Input
                            value={searchTermTemp}
                            onChange={(e) => setSearchTermTemp(e.target.value)}
                            data-skip-enter-advance
                            disabled={disabled}
                          />
                          <UiButton
                            size="icon"
                            type="submit"
                            disabled={disabled}
                            aria-label={t('common.search')}
                          >
                            <i className="pi pi-search text-base" />
                          </UiButton>
                        </div>
                      </div>
                      {isDonorSchedulingApp && (
                        <div className="flex items-center gap-2">
                          <UiCheckbox
                            id="filter-by-ranch"
                            checked={filterByRanch}
                            onCheckedChange={(checked) =>
                              setFilterByRanch(checked === true)
                            }
                          />
                          <label htmlFor="filter-by-ranch">
                            {t('farm.filterByRelatedRanch')}
                          </label>
                        </div>
                      )}
                    </form>
                  </UiCardContent>
                </UiCard>
              ) : (
                <Card className="m-2 w-fit">
                  <form
                    className="flex flex-col gap-3 p-1"
                    onSubmit={handleSearch}
                  >
                    <div className="label-input-group">
                      <label>{t('common.searchBy')}</label>
                      <Dropdown
                        value={selectedSearchBy}
                        options={SEARCH_BY_OPTIONS_WITH_ALL}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => {
                          setSelectedSearchBy(e.target.value);
                        }}
                      />
                    </div>
                    <div className="label-input-group">
                      <label>{t('common.search')}</label>
                      <div className="grid grid-cols-[auto_min-content] items-center gap-2">
                        <InputText
                          value={searchTermTemp}
                          onChange={(e) => setSearchTermTemp(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSearch(undefined);
                            }
                          }}
                          data-skip-enter-advance
                          disabled={disabled}
                          className="w-57"
                        />
                        <Button
                          icon="pi pi-search"
                          type="button"
                          onClick={() => handleSearch(undefined)}
                          disabled={disabled}
                        />
                      </div>
                    </div>
                    {isDonorSchedulingApp && (
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={filterByRanch}
                          onChange={(e) =>
                            setFilterByRanch(e.checked as boolean)
                          }
                          id="filter-by-ranch"
                        />
                        <label htmlFor="filter-by-ranch">
                          {t('farm.filterByRelatedRanch')}
                        </label>
                      </div>
                    )}
                  </form>
                </Card>
              )}
              <div className="m-2 min-h-0 flex-1">
                <WithLocalStorage
                  storageKey={storageKey}
                  onLoad={showNewlyAddedColumns}
                >
                  {persistedGrid}
                </WithLocalStorage>
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            {dialogFooterTemplate(selectedSire === null)}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
